import React, { useState } from "react";
import {
  Box,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ChartNoAxesCombined } from "lucide-react";
import { toast } from "react-toastify";
import {
  FrameIcon,
  InfoIcon,
  MyStatisticIcon,
  SettingsIcon,
  VoiceprintIcon,
  ManagerStatisticsIcon,
  AssessmentHistoryIcon,
  TeamAssessmentsIcon,
} from "../../assets/icon";
import { DayNightToggle } from "../day-night-toggle/day-night-toggle";
import useAppStore from "../../stores/appStore";
import { useUserStore } from "../../stores/userStore";
import { openCsuIntegration } from "../../axios/integration";

export type ChatInfoInitialTab =
  | undefined
  | "Lessons to answer"
  | "Crash Courses to answer";

interface UserDropdownMenuProps {
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
  onStatisticOpen: () => void;
  onAssessmentsHistoryOpen?: () => void;
  onMotivationalQuestionnaireOpen?: () => void;
  onSettingsOpen: () => void;
  onRecordingsOpen?: () => void;
  isStatic?: boolean;
  onChatInfoOpen?: (initialTab?: ChatInfoInitialTab) => void;
  onManagerStatisticsOpen?: () => void;
  onTeamAssessmentsOpen?: () => void;
}

export const UserDropdownMenu: React.FC<UserDropdownMenuProps> = ({
  anchorEl,
  isOpen,
  onClose,
  onLogout,
  onStatisticOpen,
  onAssessmentsHistoryOpen,
  onSettingsOpen,
  onRecordingsOpen,
  isStatic,
  onChatInfoOpen,
  onManagerStatisticsOpen,
  onTeamAssessmentsOpen,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { focus, isPreassessmentOpen, isAssessmentOpen } = useAppStore();
  const { user } = useUserStore();
  const isFlowBlocked = isPreassessmentOpen || isAssessmentOpen;
  const isLofremviFocus = focus.trim().toLowerCase() === "lofremvi";
  const [isCsuIntegrationOpening, setIsCsuIntegrationOpening] = useState(false);

  const typographyStyle = {
    fontSize: "14px",
    fontFamily: "Red Hat Display",
    fontWeight: 500,
    color: theme.palette.footer.menuDropdown?.textColor,
  };

  const handleCsuIntegrationOpen = async (
    target: "segmentation" | "heatMap",
  ) => {
    if (isFlowBlocked || isCsuIntegrationOpening) return;

    setIsCsuIntegrationOpening(true);
    onClose();

    try {
      await openCsuIntegration(target);
    } catch (error) {
      console.error("Failed to open CSU integration:", error);
      toast.error(
        target === "heatMap"
          ? "Could not open CSU Heat Map. Please try again."
          : "Could not open CSU Segmentation. Please try again.",
      );
    } finally {
      setIsCsuIntegrationOpening(false);
    }
  };

  if (isStatic) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.footer.menuDropdown?.bgColor,
          minWidth: "165px",
          maxWidth: "400px",
          mt: 3,
          borderRadius: "18px",
          py: "10px",
          "--Paper-overlay": "none !important",
          "&::before": {
            background: "none !important",
          },
        }}
      >
        {isMobile && (
          <Box
            sx={{
              color: "#1F2937",
              display: "flex",
              alignItems: "center",
              padding: "6px 16px",
            }}
          >
            <ListItemIcon>
              <DayNightToggle />
            </ListItemIcon>
          </Box>
        )}
        {isLofremviFocus && (
          <Box
            sx={{
              color: "#1F2937",
              display: "flex",
              alignItems: "flex-start",
              padding: "6px 16px",
            }}
          >
            <ListItemIcon sx={{ minWidth: "0", marginRight: "10px" }}>
              <ChartNoAxesCombined
                size={24}
                color="#C8CBDE"
                strokeWidth={1.75}
              />
            </ListItemIcon>
            <ListItemText
              primary="CSU Segmentation & Adoption Ladder"
              primaryTypographyProps={{ sx: typographyStyle }}
            />
          </Box>
        )}
        {/* {isLofremviFocus && (
          <Box
            sx={{
              color: "#1F2937",
              display: "flex",
              alignItems: "flex-start",
              padding: "6px 16px",
            }}
          >
            <ListItemIcon sx={{ minWidth: "0", marginRight: "10px" }}>
              <Map size={24} color="#C8CBDE" strokeWidth={1.75} />
            </ListItemIcon>
            <ListItemText
              primary="CSU Heat Map"
              primaryTypographyProps={{ sx: typographyStyle }}
            />
          </Box>
        )} */}
        {onChatInfoOpen && (
          <Box
            sx={{
              color: "#1F2937",
              display: "flex",
              alignItems: "flex-start",
              padding: "6px 16px",
              cursor: "pointer",
            }}
            onClick={() => onChatInfoOpen()}
          >
            <ListItemIcon sx={{ minWidth: "0", marginRight: "10px" }}>
              <InfoIcon
                style={{
                  width: "24px",
                  height: "24px",
                  color: "#C8CBDE",
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Interaction Management"
              primaryTypographyProps={{ sx: typographyStyle }}
            />
          </Box>
        )}
        <Box
          sx={{
            color: "#1F2937",
            display: "flex",
            alignItems: "center",
            padding: "6px 16px",
          }}
        >
          <ListItemIcon sx={{ minWidth: "0", marginRight: "10px" }}>
            <VoiceprintIcon />
          </ListItemIcon>
          <ListItemText
            primary="Roleplays"
            primaryTypographyProps={{ sx: typographyStyle }}
          />
        </Box>
        <Box
          sx={{
            color: "#1F2937",
            display: "flex",
            alignItems: "flex-start",
            padding: "6px 16px",
          }}
        >
          <ListItemIcon sx={{ minWidth: "0", marginRight: "10px" }}>
            <MyStatisticIcon />
          </ListItemIcon>
          <ListItemText
            primary="My Statistics"
            primaryTypographyProps={{ sx: typographyStyle }}
          />
        </Box>
        {onAssessmentsHistoryOpen && (
          <Box
            sx={{
              color: "#1F2937",
              display: "flex",
              alignItems: "flex-start",
              padding: "6px 16px",
              cursor: "pointer",
            }}
            onClick={() => onAssessmentsHistoryOpen()}
          >
            <ListItemIcon sx={{ minWidth: "0", marginRight: "10px" }}>
              <AssessmentHistoryIcon
                size={24}
                color="#C8CBDE"
                strokeWidth={1.75}
              />
            </ListItemIcon>
            <ListItemText
              primary="Contests history"
              primaryTypographyProps={{ sx: typographyStyle }}
            />
          </Box>
        )}
        {/* Team Statistics - only for managers */}
        {user?.is_manager && (
          <Box
            sx={{
              color: "#1F2937",
              display: "flex",
              alignItems: "flex-start",
              padding: "6px 16px",
            }}
          >
            <ListItemIcon sx={{ minWidth: "0", marginRight: "10px" }}>
              <ManagerStatisticsIcon />
            </ListItemIcon>
            <ListItemText
              primary="Team Statistics"
              primaryTypographyProps={{ sx: typographyStyle }}
            />
          </Box>
        )}
        {user?.is_manager && onTeamAssessmentsOpen && (
          <Box
            sx={{
              color: "#1F2937",
              display: "flex",
              alignItems: "flex-start",
              padding: "6px 16px",
              cursor: "pointer",
            }}
            onClick={() => onTeamAssessmentsOpen()}
          >
            <ListItemIcon sx={{ minWidth: "0", marginRight: "10px" }}>
              <TeamAssessmentsIcon
                size={24}
                color="#C8CBDE"
                strokeWidth={1.75}
              />
            </ListItemIcon>
            <ListItemText
              primary="My Team Contest Statistics"
              primaryTypographyProps={{ sx: typographyStyle }}
            />
          </Box>
        )}
        <Box
          sx={{
            color: "#1F2937",
            display: "flex",
            alignItems: "center",
            padding: "6px 16px",
          }}
        >
          <ListItemIcon sx={{ minWidth: "0", marginRight: "10px" }}>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText
            primary="Settings"
            primaryTypographyProps={{ sx: typographyStyle }}
          />
        </Box>
        <Box
          sx={{
            color: "#1F2937",
            display: "flex",
            alignItems: "center",
            padding: "6px 16px",
          }}
        >
          <ListItemIcon sx={{ minWidth: "0", marginRight: "10px" }}>
            <FrameIcon />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{ sx: typographyStyle }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Menu
      anchorEl={anchorEl}
      open={isOpen}
      onClose={onClose}
      disableAutoFocus
      disableEnforceFocus
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      sx={{
        "& .MuiPaper-root": {
          backgroundColor: theme.palette.footer.menuDropdown?.bgColor,
          minWidth: "165px",
          maxWidth: "400px",
          mt: 3,
          borderRadius: "18px",
          paddingY: "10px",
          "--Paper-overlay": "none !important",
          "&::before": {
            background: "none !important",
          },
        },
      }}
    >
      <div>
        {isMobile && (
          <MenuItem onClick={() => {}} sx={{ color: "#1F2937" }}>
            <ListItemIcon>
              <DayNightToggle />
            </ListItemIcon>
          </MenuItem>
        )}
        {isLofremviFocus && (
          <MenuItem
            onClick={() => handleCsuIntegrationOpen("segmentation")}
            disabled={isFlowBlocked || isCsuIntegrationOpening}
            sx={{ color: "#1F2937" }}
          >
            <ListItemIcon>
              <ChartNoAxesCombined
                size={24}
                color="#C8CBDE"
                strokeWidth={1.75}
              />
            </ListItemIcon>
            <ListItemText
              primary="CSU Segmentation & Adoption Ladder"
              primaryTypographyProps={{ sx: typographyStyle }}
            />
          </MenuItem>
        )}
        {/* {isLofremviFocus && (
          <MenuItem
            onClick={() => handleCsuIntegrationOpen("heatMap")}
            disabled={isFlowBlocked || isCsuIntegrationOpening}
            sx={{ color: "#1F2937" }}
          >
            <ListItemIcon>
              <Map size={24} color="#C8CBDE" strokeWidth={1.75} />
            </ListItemIcon>
            <ListItemText
              primary="CSU Heat Map"
              primaryTypographyProps={{ sx: typographyStyle }}
            />
          </MenuItem>
        )} */}
        {onChatInfoOpen && (
          <MenuItem
            onClick={() => {
              if (isFlowBlocked) return;
              onChatInfoOpen();
              onClose();
            }}
            disabled={isFlowBlocked}
            sx={{ color: "#1F2937" }}
          >
            <ListItemIcon>
              <InfoIcon
                style={{
                  width: "24px",
                  height: "24px",
                  color: "#C8CBDE",
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Interaction Management"
              primaryTypographyProps={{ sx: typographyStyle }}
            />
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (isFlowBlocked) return;
            onRecordingsOpen?.();
            onClose();
          }}
          disabled={isFlowBlocked}
          sx={{ color: "#1F2937" }}
        >
          <ListItemIcon>
            <VoiceprintIcon
              fill="#C8CBDE"
              style={{ width: "24px", height: "24px" }}
            />
          </ListItemIcon>
          <ListItemText
            primary="Roleplays"
            primaryTypographyProps={{ sx: typographyStyle }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (isFlowBlocked) return;
            onStatisticOpen();
          }}
          disabled={isFlowBlocked}
          sx={{ color: "#1F2937" }}
        >
          <ListItemIcon>
            <MyStatisticIcon />
          </ListItemIcon>
          <ListItemText
            primary="My Statistics"
            primaryTypographyProps={{ sx: typographyStyle }}
          />
        </MenuItem>
        {onAssessmentsHistoryOpen && (
          <MenuItem
            onClick={() => {
              if (isFlowBlocked) return;
              onAssessmentsHistoryOpen();
              onClose();
            }}
            disabled={isFlowBlocked}
            sx={{ color: "#1F2937" }}
          >
            <ListItemIcon>
              <AssessmentHistoryIcon
                size={24}
                color="#C8CBDE"
                strokeWidth={1.75}
              />
            </ListItemIcon>
            <ListItemText
              primary="Contests history"
              primaryTypographyProps={{ sx: typographyStyle }}
            />
          </MenuItem>
        )}
        {/* Team Statistics - only for managers */}
        {user?.is_manager && onManagerStatisticsOpen && (
          <MenuItem
            onClick={() => {
              if (isFlowBlocked) return;
              onManagerStatisticsOpen();
              onClose();
            }}
            disabled={isFlowBlocked}
            sx={{ color: "#1F2937" }}
          >
            <ListItemIcon>
              <ManagerStatisticsIcon />
            </ListItemIcon>
            <ListItemText
              primary="Team Statistics"
              primaryTypographyProps={{ sx: typographyStyle }}
            />
          </MenuItem>
        )}
        {user?.is_manager && onTeamAssessmentsOpen && (
          <MenuItem
            onClick={() => {
              if (isFlowBlocked) return;
              onTeamAssessmentsOpen();
              onClose();
            }}
            disabled={isFlowBlocked}
            sx={{ color: "#1F2937" }}
          >
            <ListItemIcon>
              <TeamAssessmentsIcon
                size={24}
                color="#C8CBDE"
                strokeWidth={1.75}
              />
            </ListItemIcon>
            <ListItemText
              primary="My Team Contest Statistics"
              primaryTypographyProps={{ sx: typographyStyle }}
            />
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (isAssessmentOpen) return;
            onSettingsOpen();
            onClose();
          }}
          disabled={isAssessmentOpen}
          sx={{ color: "#1F2937" }}
        >
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText
            primary="Settings"
            primaryTypographyProps={{ sx: typographyStyle }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            onClose();
            onLogout();
          }}
          sx={{ color: "#1F2937" }}
        >
          <ListItemIcon>
            <FrameIcon />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{ sx: typographyStyle }}
          />
        </MenuItem>
      </div>
    </Menu>
  );
};
